import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import {
  type AresCompanyResult,
  type ExhibitionSignUpDto,
} from '@paladin-monorepo/common';

import API from '@/lib/api';
import { invalidateAllQueries } from '@/lib/queryCache';
import { trackEvent } from '@/lib/analytics';
import { useLanguage } from '@/hooks/useLanguage';
import { AuthLayout } from '@/layouts/AuthLayout';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from '@/components/ui/combobox';
import { useAresCompanySearch } from '@/hooks/useAresCompanySearch';

interface ExhibitionSignUpResponseBody {
  created: boolean;
  accountType: string;
}

const EXHIBITION_IDLE_MS = 45_000;

export function ExhibitionPage() {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [selectedCompany, setSelectedCompany] = useState<AresCompanyResult | undefined>();
  const [manualMode, setManualMode] = useState(false);
  const [manualName, setManualName] = useState('');
  const [visitorName, setVisitorName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [coverVisible, setCoverVisible] = useState(true);
  const [formKey, setFormKey] = useState(0);
  const startButtonRef = useRef<HTMLButtonElement>(null);

  const {
    results: aresResults,
    setResults: setAresResults,
    isSearching,
    search,
    skipNextSearchRef,
  } = useAresCompanySearch({ debounceMs: 350 });

  const clearForm = useCallback(() => {
    search.cancel();
    setSelectedCompany(undefined);
    setManualMode(false);
    setManualName('');
    setVisitorName('');
    setValidationError(null);
    setAlreadyRegistered(false);
    setAresResults([]);
    setFormKey((key) => key + 1);
  }, [search, setAresResults]);

  useEffect(() => {
    if (coverVisible || isSubmitting) {
      return;
    }

    let timeoutId = window.setTimeout(() => {
      clearForm();
      setCoverVisible(true);
    }, EXHIBITION_IDLE_MS);

    const resetIdleTimer = () => {
      window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => {
        clearForm();
        setCoverVisible(true);
      }, EXHIBITION_IDLE_MS);
    };

    const eventNames = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart'] as const;
    for (const eventName of eventNames) {
      window.addEventListener(eventName, resetIdleTimer, { passive: true });
    }

    return () => {
      window.clearTimeout(timeoutId);
      for (const eventName of eventNames) {
        window.removeEventListener(eventName, resetIdleTimer);
      }
    };
  }, [clearForm, coverVisible, isSubmitting]);

  useEffect(() => {
    if (!coverVisible) {
      return;
    }
    startButtonRef.current?.focus();
  }, [coverVisible]);

  const buildPayload = (): ExhibitionSignUpDto | null => {
    if (manualMode) {
      const name = manualName.trim();
      if (!name) {
        setValidationError(t('exhibition.form.errors.companyRequired'));
        return null;
      }
      return {
        company: { name },
        language,
        ...(visitorName.trim() ? { visitorName: visitorName.trim() } : {}),
      };
    }

    if (!selectedCompany) {
      setValidationError(t('exhibition.form.errors.companyRequired'));
      return null;
    }
    const addr = [
      selectedCompany.sidlo.cisloDomovni,
      `${selectedCompany.sidlo.cisloOrientacni ?? ''}${selectedCompany.sidlo.cisloOrientacniPismeno ?? ''}`,
    ]
      .filter(Boolean)
      .join('/');
    return {
      companyId: selectedCompany.ico,
      company: {
        name: selectedCompany.obchodniJmeno,
        ...(selectedCompany.dic ? { vatId: selectedCompany.dic } : {}),
        address: {
          street: `${selectedCompany.sidlo.nazevUlice ?? ''} ${addr}`.trim(),
          city: selectedCompany.sidlo.nazevObce ?? '',
          zip: selectedCompany.sidlo.psc?.toString() ?? '',
          country: selectedCompany.sidlo.kodStatu ?? '',
        },
      },
      language,
      ...(visitorName.trim() ? { visitorName: visitorName.trim() } : {}),
    };
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    setValidationError(null);

    const payload = buildPayload();
    if (!payload) return;

    setIsSubmitting(true);
    try {
      await API.get('/auth/csrf-token');
      const response = await API.post<ExhibitionSignUpResponseBody>(
        '/auth/exhibition-signup',
        payload,
      );
      if (response.data.created) {
        trackEvent('sign_up', { method: 'exhibition' });
      } else {
        trackEvent('login', { method: 'exhibition' });
      }
      await invalidateAllQueries(queryClient);
      navigate(
        response.data.created ? '/exhibition/start?exhibition=1' : '/exhibition/start',
        { replace: true },
      );
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 409) {
        setAlreadyRegistered(true);
      } else {
        trackEvent('login_failed', { method: 'exhibition' });
        toast.error(t('exhibition.errors.default.title'), {
          description: t('exhibition.errors.default.description'),
        });
      }
      setIsSubmitting(false);
    }
  };

  const page = alreadyRegistered ? (
      <AuthLayout>
        <Card className="px-4 py-8 min-w-[320px] sm:min-w-[480px] max-w-[520px]">
          <CardHeader>
            <CardTitle className="text-xl">{t('exhibition.alreadyRegistered.title')}</CardTitle>
            <CardDescription>{t('exhibition.alreadyRegistered.description')}</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4">
            <Button onClick={() => navigate('/auth/login')}>
              {t('exhibition.alreadyRegistered.loginCta')}
            </Button>
            <Button variant="ghost" onClick={() => setAlreadyRegistered(false)}>
              {t('exhibition.alreadyRegistered.back')}
            </Button>
          </CardContent>
        </Card>
      </AuthLayout>
  ) : isSubmitting ? (
      <AuthLayout hideFooter>
        <Card className="px-4 py-12 min-w-[320px] sm:min-w-[480px] max-w-[520px]">
          <CardContent className="flex flex-col items-center gap-4 text-center">
            <Loader2 className="h-10 w-10 animate-spin text-primary" />
            <p className="text-lg font-semibold">{t('exhibition.loading.title')}</p>
            <p className="text-sm text-muted-foreground">{t('exhibition.loading.description')}</p>
          </CardContent>
        </Card>
      </AuthLayout>
  ) : (
    <AuthLayout>
      <Card className="px-4 py-8 min-w-[320px] sm:min-w-[480px] max-w-[520px]">
        <CardHeader>
          <CardTitle className="text-xl">{t('exhibition.title')}</CardTitle>
          <CardDescription>{t('exhibition.description')}</CardDescription>
        </CardHeader>
        <CardContent>
          <form key={formKey} onSubmit={onSubmit} className="flex flex-col gap-6">
            {!manualMode && selectedCompany && (
              <div className="flex flex-col gap-1">
                <Card className="p-2 bg-muted">
                  <CardContent className="px-2">
                    <span className="text-caption text-muted-foreground">{selectedCompany.ico}</span>
                    <br />
                    {selectedCompany.obchodniJmeno}
                    <br />
                    {selectedCompany.sidlo.textovaAdresa}
                  </CardContent>
                </Card>
                <button
                  type="button"
                  className="self-start text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground transition-colors"
                  onClick={() => {
                    setSelectedCompany(undefined);
                    setAresResults([]);
                  }}
                >
                  {t('exhibition.form.company.changeSelection')}
                </button>
              </div>
            )}

            {!manualMode && !selectedCompany && (
              <div className="flex flex-col gap-2">
                <Label>{t('exhibition.form.company.label')}</Label>
                <Combobox<AresCompanyResult>
                  items={aresResults}
                  itemToStringValue={(item: AresCompanyResult) => item.ico}
                  onValueChange={(value: AresCompanyResult | null) => {
                    if (value) {
                      skipNextSearchRef.current = true;
                      setSelectedCompany(value);
                      setValidationError(null);
                      setAresResults([]);
                    }
                  }}
                  onInputValueChange={(inputValue: string) => {
                    if (inputValue !== '') {
                      search(inputValue);
                    }
                  }}
                >
                  <ComboboxInput
                    placeholder={t('exhibition.form.company.placeholder')}
                    showTrigger={false}
                  />
                  <ComboboxContent>
                    <ComboboxEmpty>
                      {isSearching
                        ? t('exhibition.form.company.searching')
                        : t('exhibition.form.company.noResults')}
                    </ComboboxEmpty>
                    <ComboboxList>
                      {(_item) => {
                        const item = _item as AresCompanyResult;
                        return (
                          <ComboboxItem
                            key={item.ico}
                            value={item}
                            className="flex-col items-start py-2"
                          >
                            <span className="text-xs text-muted-foreground">{item.ico}</span>
                            <span className="font-medium">{item.obchodniJmeno}</span>
                            {item.sidlo.textovaAdresa && (
                              <span className="text-xs text-muted-foreground">
                                {item.sidlo.textovaAdresa}
                              </span>
                            )}
                          </ComboboxItem>
                        );
                      }}
                    </ComboboxList>
                  </ComboboxContent>
                </Combobox>
              </div>
            )}

            {manualMode && (
              <div className="flex flex-col gap-2">
                <Label htmlFor="exhibition-company-name">
                  {t('exhibition.form.manualName.label')}
                </Label>
                <Input
                  id="exhibition-company-name"
                  value={manualName}
                  onChange={(e) => {
                    setManualName(e.target.value);
                    setValidationError(null);
                  }}
                  placeholder={t('exhibition.form.manualName.placeholder')}
                  autoComplete="organization"
                />
              </div>
            )}

            <button
              type="button"
              className="self-start text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground transition-colors"
              onClick={() => {
                setManualMode((prev) => !prev);
                setSelectedCompany(undefined);
                setValidationError(null);
              }}
            >
              {manualMode
                ? t('exhibition.form.backToSearch')
                : t('exhibition.form.companyNotFound')}
            </button>

            <div className="flex flex-col gap-2">
              <Label htmlFor="exhibition-visitor-name">
                {t('exhibition.form.visitorName.label')}
              </Label>
              <Input
                id="exhibition-visitor-name"
                value={visitorName}
                onChange={(e) => setVisitorName(e.target.value)}
                placeholder={t('exhibition.form.visitorName.placeholder')}
                autoComplete="name"
              />
            </div>

            {validationError && <p className="text-sm text-destructive">{validationError}</p>}

            <Button className="py-5" type="submit">
              {t('exhibition.form.submit')}
            </Button>
          </form>
        </CardContent>
      </Card>
    </AuthLayout>
  );

  const startWords = t('exhibition.cover.startHere').split(/\s+/).filter(Boolean);

  return (
    <>
      <div inert={coverVisible ? true : undefined}>{page}</div>
      <div
        aria-hidden={!coverVisible}
        inert={coverVisible ? undefined : true}
        className={cn(
          'fixed inset-0 z-50 transition-opacity duration-700',
          coverVisible ? 'opacity-100' : 'pointer-events-none opacity-0',
        )}
      >
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(90deg, var(--brand-primary) 50%, var(--brand-secondary) 50%)',
          }}
        />
        <div className="absolute top-4 right-4 z-10">
          <LanguageSwitcher className="max-w-[120px]" />
        </div>
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <button
            ref={startButtonRef}
            type="button"
            onClick={() => setCoverVisible(false)}
            className="pointer-events-auto relative flex size-72 cursor-pointer items-center justify-center overflow-hidden rounded-full text-center shadow-lg transition-transform hover:scale-105 active:scale-95 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-white/80"
            style={{
              background:
                'linear-gradient(90deg, var(--brand-secondary) 50%, var(--brand-primary) 50%)',
            }}
          >
            <span className="flex flex-col items-center justify-center gap-1 text-display-2 text-block-ink">
              {startWords.map((word) => (
                <span key={word} className="relative isolate whitespace-nowrap text-center">
                  <span
                    aria-hidden
                    className="pointer-events-none absolute inset-0 z-0 select-none text-white/50 blur-[2px]"
                    style={{ WebkitTextStroke: '5px rgb(255 255 255 / 0.15)' }}
                  >
                    {word}
                  </span>
                  <span className="relative z-10">{word}</span>
                </span>
              ))}
            </span>
          </button>
        </div>
      </div>
    </>
  );
}

export default ExhibitionPage;
